import React, { FC, ReactNode, useCallback, useContext } from 'react';
import find from 'lodash/find';
import get from 'lodash/get';
import set from 'lodash/set';
import { produce } from 'immer';

import FieldContainer from './FieldContainer';
import FormContext from 'src/presentation/contexts/FormContext';
import { MultiselectChips as ADMultiselectChips } from 'src/presentation/components/agent_desktop_components';

type MultiselectChipsProps = {
  'data-aid'?: string;
  attr: string;
  className?: string;
  delay?: number; // delay in ms for search input debounce
  description?: string;
  disabled?: boolean;
  isLoading?: boolean;
  label: string | ReactNode;
  labelsHash?: Record<string, string>;
  maxRows?: number;
  onSearchChanged?: Function;
  onValuesChanged?: Function;
  options?: any[];
  placeholder?: string;
  removableOptions?: any[];
  values?: string[];
};

const MultiselectChips: FC<MultiselectChipsProps & any> = ({
  'data-aid': dataAid = null,
  attr,
  className,
  delay,
  description,
  disabled,
  isLoading,
  label,
  labelsHash = null,
  maxRows,
  onSearchChanged,
  onValuesChanged,
  options,
  placeholder,
  removableOptions,
  values,
}) => {
  const { errors, workingState, updateState, resetError, onBlur } = useContext(FormContext);
  const error = find(errors, error => error.attr === attr);
  const onValuesChangedHandler = useCallback(
    values => {
      if (onValuesChanged) {
        onValuesChanged(values);
      } else {
        resetError({ attr, errors });
        updateState(set({}, attr, values));
        onBlur && onBlur({ currentState: produce(workingState, draftState => set(draftState, attr, values)) });
      }
    },
    [attr, errors, onValuesChanged, resetError, updateState, workingState, onBlur]
  );

  const workingValues = values ?? get(workingState, attr);

  return (
    <FieldContainer
      className={className}
      data-aid={dataAid}
      description={description}
      label={label}
      preventContentFromOverflow // prevents the container from overflowing with wide content
      control={
        <ADMultiselectChips
          data-aid={dataAid}
          delay={delay}
          disabled={disabled}
          error={get(error, 'detail')}
          isLoading={isLoading}
          labelsHash={labelsHash}
          maxRows={maxRows}
          onSearchChanged={onSearchChanged}
          onValuesChanged={onValuesChangedHandler}
          options={options}
          placeholder={placeholder}
          removableOptions={removableOptions}
          values={workingValues}
        />
      }
    />
  );
};

export default MultiselectChips;
